import Card from '@10xLabs/react-web/components/Card'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import FormLayout from 'components/v2/FormLayout'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { NewPlace } from 'features/Places/components/NewPlace'
import { INewPlaceForm } from 'features/Places/types'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { GET_TPLACE } from 'features/Places/queries-legacy'
import { CHANGE_PLACE } from 'features/Places/mutations-legacy'
import { GTChangePlaceV2Input, GTPlaceV2AmenityInput, GTPlaceV2TypeInput, GTTPlace } from 'gql/types.generated'
import { messages as fileMessages } from 'messages/file'
import { useLogin } from 'features/Login/hooks'
import { getDifference } from 'utils/object'
import { canWrite } from 'features/Users/utils'
import { usePushFileToS3 } from 'utils/file'

const messages = defineMessages({
  helmet: {
    id: 'places.edit.helmet',
    defaultMessage: 'Edit Place | Nexbus Admin'
  }
})

const EditPlaceView = (): ReactElement => {
  const navigate = useNavigate()
  const { placeId } = useParams()
  const intl = useIntl()
  const pushFileToS3 = usePushFileToS3()
  const login = useLogin()
  const [s3Error, setS3Error] = useState<string | undefined>()

  let content

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery<{ tplace: GTTPlace }>(GET_TPLACE, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      placeId
    }
  })
  const [changePlace, { error: mutationError }] = useMutation(CHANGE_PLACE)

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  useEffect((): void => {
    refetch()
  }, [])

  const handleSubmit = async (
    place: INewPlaceForm,
    { setSubmitting }: { setSubmitting: (bool: boolean) => void }
  ): Promise<void> => {
    const oldPlace: GTChangePlaceV2Input = {
      name: queryData?.tplace.name,
      param: queryData?.tplace.param,
      type: queryData?.tplace.type as unknown as GTPlaceV2TypeInput,
      tags: queryData?.tplace.tags,
      address: {
        line1: queryData?.tplace.address?.line1,
        line2: queryData?.tplace.address?.line2 || null,
        zipCode: queryData?.tplace.address?.zipCode,
        cityName: queryData?.tplace.address?.cityName,
        stateCode: queryData?.tplace.address?.stateCode,
        countryCode: queryData?.tplace.address?.countryCode
      },
      coordinates: queryData?.tplace.coordinates,
      geofenceRadius: queryData?.tplace.geofenceRadius || null,
      area: queryData?.tplace.area || null,
      directions: queryData?.tplace.directions || null,
      amenities: queryData?.tplace.amenities as unknown as GTPlaceV2AmenityInput[],
      photoPath: queryData?.tplace.photo?.path || null
    }

    let photoPath = place.photo?.path || null
    if (place.photo.file) {
      try {
        photoPath = await pushFileToS3(place.photo.file)
      } catch (ex) {
        setS3Error(intl.formatMessage(fileMessages.uploadFileError, { count: 1 }))
        setSubmitting(false)
        return
      }
    }
    const newPlace: GTChangePlaceV2Input = {
      name: place.name,
      param: place.param,
      type: place.type,
      tags: place.tags,
      address: {
        line1: place.address.line1,
        line2: place.address.line2 || null,
        zipCode: place.address.zipCode,
        cityName: place.city.name,
        stateCode: place.city.stateCode,
        countryCode: place.address.countryCode
      },
      coordinates: place.coordinates,
      geofenceRadius: place.geofenceRadius || null,
      area: place.area || null,
      directions: place.directions || null,
      amenities: place.amenities.map((amenity) => amenity.id) as GTPlaceV2AmenityInput[],
      photoPath
    }
    const payload = getDifference(newPlace, oldPlace)
    if (isEmpty(payload)) {
      navigate('/places')
    } else {
      try {
        await changePlace({
          variables: {
            id: placeId,
            input: {
              ...payload,
              address: {
                ...payload.address,
                line2: newPlace.address?.line2
              },
              area: newPlace.area,
              directions: newPlace.directions,
              photoPath
            }
          }
        })
        navigate('/places')
      } catch (ex) {
        // DO NOTHING
      }
    }
    setSubmitting(false)
  }

  if (!canWrite(FEATURES.PLACE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.PLACE} />
  }

  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="places.edit.error.description"
              defaultMessage="There was an error while loading your place"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button onClick={(): TSFixMe => refetch()} kind="primary">
              <FormattedMessage id="places.edit.error.action" defaultMessage="Reload your place" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = (
      <NewPlace
        onSubmit={handleSubmit}
        place={queryData?.tplace as GTTPlace}
        error={mutationError}
        s3Error={s3Error}
        isEdit
      />
    )
  }

  return <FormLayout.Container>{content}</FormLayout.Container>
}

export default withLastUrl(EditPlaceView)
